import { defineStore } from 'pinia'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { GlobalError } from '@/utils/globalError/globalError'
import type { GetOverheadImageRequest } from '@/types/api/geoAerialPhoto/geoAerialPhotoTypes'
import { geoAerialPhotoApi } from '@/api/geoAerialPhoto/geoAerialPhotoApiGwFuncs'
import { useS3Accessor } from '@/features/s3/useS3Accessor'
import MiiaFwConfig from '@/configs/config'

export interface ImageUrl {
  imageUrl: string
  thumbnail: string
}

// State 用 TypeScript 型定義
interface OverheadImageUrlsState {
  /** 空撮画像のS3URLリスト */
  imageUrls: ImageUrl[]
  zoomImage: string
  govCode: string
  disasterId: string
  flightId: string
}

const initialState: OverheadImageUrlsState = {
  imageUrls: [],
  zoomImage: '',
  govCode: '',
  disasterId: '',
  flightId: ''
}

/**
 * S3から空撮画像一覧を取得する
 * @param {string} imageUrl
 * @returns {Promise<string>} 空撮画像のblob
 */
const getOverheadImageUrls = async (params: GetOverheadImageRequest): Promise<ImageUrl[]> => {
  if (params.govCode === undefined || params.disasterId === undefined)
    throw new GlobalError([ERROR_CODES.GET_AERIAL_AREAS_FETCH_FAILED])
  const { getOverheadImage } = geoAerialPhotoApi()
  const res = await getOverheadImage(params)
  if (res.error !== undefined) {
    throw new GlobalError([ERROR_CODES.GET_AERIAL_AREAS_FETCH_FAILED])
  }
  // S3URLを作成し、画像を取得
  const imageUrls = await Promise.all(
    res.result.imageUrls.map(async imageUrl => {
      const imageUrlThumbnail = imageUrl.replace('.jpg', '_thumbnail.jpg')
      const thumbnail = await getOverheadImageUrl(imageUrlThumbnail)
      return {
        imageUrl: imageUrl,
        thumbnail: thumbnail
      }
    })
  )
  return imageUrls
}

/**
 * S3から空撮画像を取得する
 * @param {string} imageUrl
 * @returns {Promise<string>} 空撮画像のblob
 */
const getOverheadImageUrl = async (imageUrl: string): Promise<string> => {
  try {
    const path = imageUrl.startsWith('/') ? imageUrl.slice(1) : imageUrl
    const { getFileFromS3 } = useS3Accessor()
    const file = await getFileFromS3(
      path,
      null,
      MiiaFwConfig.config.s3Access.contentsBucketScreeningGeoResult
    )
    return URL.createObjectURL(file)
  } catch (err) {
    console.error(err)
    throw new GlobalError([ERROR_CODES.GET_AERIAL_AREAS_FETCH_FAILED])
  }
}

// Pinia Store の定義
export const useOverheadImageStore = defineStore('overheadImage', {
  state: (): OverheadImageUrlsState => ({ ...initialState }),

  getters: {
    /**
     * 空撮画像のS3URLリストを取得する
     * @param {OverheadImageUrlsState} state
     * @returns {string[]} 空撮画像のS3URLリスト
     */
    getImageUrls: (state: OverheadImageUrlsState): ImageUrl[] => {
      return state.imageUrls
    },
    /**
     * 空撮画像を取得する
     * @param {OverheadImageUrlsState} state
     * @returns {string} 空撮画像
     */
    getZoomImage: (state: OverheadImageUrlsState): string => {
      return state.zoomImage
    },
    /**
     * 空撮画像のS3URLリストに紐づく建物IDを取得する
     * @param {OverheadImageUrlsState} state
     * @returns {string} フライトID
     */
    getFlightId: (state: OverheadImageUrlsState): string => {
      return state.flightId
    }
  },
  actions: {
    /**
     * State のすべての値を初期化
     */
    reset() {
      Object.assign(this.$state, initialState)
    },

    /**
     * APIからデータを取得
     * @param params APIリクエスト
     * @return {} - 戻り値なし
     */
    async fetch(params: GetOverheadImageRequest): Promise<void> {
      const imageUrls = await getOverheadImageUrls(params)
      this.imageUrls = imageUrls
      this.govCode = params.govCode!
      this.disasterId = params.disasterId!
      this.flightId = params.flightId
    },

    /**
     * 空撮画像のURLを削除する
     * @return {} - 戻り値なし
     */
    removeImageUrls(): void {
      for (const imageUrl of this.imageUrls) {
        URL.revokeObjectURL(imageUrl.thumbnail)
      }
      URL.revokeObjectURL(this.zoomImage)
      this.imageUrls = []
      this.zoomImage = ''
      this.govCode = ''
      this.disasterId = ''
      this.flightId = ''
    },

    /**
     * 空撮画像の拡大画像を取得してstoreに保存する
     * @param zoomImageUrl 空撮画像の拡大画像URL
     * @return {} - 戻り値なし
     */
    async setZoomImage(zoomImageUrl: string): Promise<void> {
      const zoomImage = await getOverheadImageUrl(zoomImageUrl)
      this.zoomImage = zoomImage
    }
  }
})
